// 共享工具函数和常量

// 允许的图片类型
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];

// 允许的图片扩展名
export const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];

// 验证URL是否有效
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// 检查是否为图片URL
export const isImageUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();
    return ALLOWED_IMAGE_EXTENSIONS.some((ext) => pathname.endsWith(ext));
  } catch {
    return false;
  }
};

// 比例规范化常量和函数
export const ASPECT_RATIO_LIMITS = {
  MIN: 9 / 21, // 9:21 ≈ 0.43 (最高比例)
  MAX: 21 / 9, // 21:9 ≈ 2.33 (最宽比例)
} as const;

// 判断图片比例超出类型
export const getAspectRatioViolationType = (width: number, height: number): 'too-wide' | 'too-tall' | 'valid' => {
  const ratio = width / height;
  if (ratio > ASPECT_RATIO_LIMITS.MAX) return 'too-wide';
  if (ratio < ASPECT_RATIO_LIMITS.MIN) return 'too-tall';
  return 'valid';
};

// 计算添加白边后的目标尺寸
export const calculateTargetDimensions = (
  originalWidth: number,
  originalHeight: number,
): { width: number; height: number } => {
  const violationType = getAspectRatioViolationType(originalWidth, originalHeight);

  if (violationType === 'valid') {
    return { width: originalWidth, height: originalHeight };
  }

  if (violationType === 'too-wide') {
    // 图片太宽，添加上下白边，限制到最大比例
    const targetHeight = Math.round(originalWidth / ASPECT_RATIO_LIMITS.MAX);
    return { width: originalWidth, height: targetHeight };
  } else {
    // 图片太高，添加左右白边，限制到最小比例
    const targetWidth = Math.round(originalHeight * ASPECT_RATIO_LIMITS.MIN);
    return { width: targetWidth, height: originalHeight };
  }
};

// 为图片添加白边规范化比例并应用像素数控制
export const normalizeImageAspectRatio = async (
  imageDataUrl: string,
  originalWidth: number,
  originalHeight: number,
  usePixelControl: boolean = false,
): Promise<{ dataUrl: string; width: number; height: number }> => {
  // 第一步：先进行白边填充（比例规范化）
  const violationType = getAspectRatioViolationType(originalWidth, originalHeight);

  let normalizedDataUrl = imageDataUrl;
  let normalizedWidth = originalWidth;
  let normalizedHeight = originalHeight;

  if (violationType !== 'valid') {
    // 需要添加白边进行比例规范化
    const targetDimensions = calculateTargetDimensions(originalWidth, originalHeight);

    const result = await new Promise<{ dataUrl: string; width: number; height: number }>((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('无法创建Canvas上下文'));
        return;
      }

      canvas.width = targetDimensions.width;
      canvas.height = targetDimensions.height;

      // 填充白色背景
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const img = new Image();
      img.onload = () => {
        // 计算原图在新Canvas中的居中位置
        const offsetX = Math.round((targetDimensions.width - originalWidth) / 2);
        const offsetY = Math.round((targetDimensions.height - originalHeight) / 2);

        // 绘制原图到居中位置
        ctx.drawImage(img, offsetX, offsetY, originalWidth, originalHeight);

        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        resolve({
          dataUrl,
          width: targetDimensions.width,
          height: targetDimensions.height,
        });
      };

      img.onerror = () => reject(new Error('图片加载失败'));
      img.src = imageDataUrl;
    });

    normalizedDataUrl = result.dataUrl;
    normalizedWidth = result.width;
    normalizedHeight = result.height;
  }

  // 第二步：如果启用像素数控制，对规范化后的图片进行像素数控制
  if (usePixelControl) {
    const { scaleImageToPixelTarget } = await import('./pixel-control-utils');
    return scaleImageToPixelTarget(normalizedDataUrl, normalizedWidth, normalizedHeight);
  }

  // 返回规范化后的结果
  return {
    dataUrl: normalizedDataUrl,
    width: normalizedWidth,
    height: normalizedHeight,
  };
};

// 事件处理工具函数
export const preventDefault = (e: React.MouseEvent | React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

// 创建事件处理器
export const createEventHandler = (callback: () => void) => {
  return (e: React.MouseEvent) => {
    preventDefault(e);
    callback();
  };
};
